import { DispatchRow, formatAddress, userLabel, applyCoordinates, extractPartnerId } from './dispatchTypes';

function enrichRow(row: DispatchRow): DispatchRow {
  const fare = row.raw.fare as { paymentMethod?: string } | undefined;
  return {
    ...row,
    partnerId: extractPartnerId(row.raw, row.service),
    scheduledAt: row.raw.scheduledAt ? String(row.raw.scheduledAt) : undefined,
    vehicleType: String(row.raw.vehicleType || row.raw.vehicleTypeCode || '') || undefined,
    paymentMethod: String(row.raw.paymentMethod || fare?.paymentMethod || '') || undefined,
  };
}

export function buildDispatchRows(
  rides: Record<string, unknown>[] | undefined,
  foodOrders: Record<string, unknown>[] | undefined,
  groceryOrders: Record<string, unknown>[] | undefined,
  porterOrders?: Record<string, unknown>[] | undefined,
  onDemandBookings?: Record<string, unknown>[] | undefined,
): DispatchRow[] {
  const all: DispatchRow[] = [];

  (rides || []).forEach((r) => {
    const cust = userLabel(r.customerId);
    const driver = userLabel(r.driverId);
    const pickup = r.pickup as { address?: string; coordinates?: number[] } | undefined;
    const dropoff = r.dropoff as { address?: string; coordinates?: number[] } | undefined;
    all.push({
      _id: r._id as string,
      service: 'transportation',
      ref: String(r.rideNumber),
      customer: cust.name,
      customerPhone: cust.phone,
      partner: driver.name === '-' ? 'Unassigned' : driver.name,
      status: String(r.status),
      total: (r.fare as { total?: number })?.total || 0,
      createdAt: String(r.createdAt),
      pickupAddress: pickup?.address,
      dropoffAddress: dropoff?.address,
      pickupLat: pickup?.coordinates?.[1],
      pickupLng: pickup?.coordinates?.[0],
      dropoffLat: dropoff?.coordinates?.[1],
      dropoffLng: dropoff?.coordinates?.[0],
      raw: r,
    });
  });

  (foodOrders || []).forEach((o) => {
    const cust = userLabel(o.customerId);
    const partner = userLabel(o.deliveryPartnerId);
    const row: DispatchRow = {
      _id: o._id as string,
      service: 'food',
      ref: String(o.orderNumber),
      customer: cust.name,
      customerPhone: cust.phone,
      partner: partner.name !== '-' ? partner.name : ((o.restaurantId as { name?: string })?.name || 'Unassigned'),
      status: String(o.status),
      total: Number(o.total || 0),
      createdAt: String(o.createdAt),
      dropoffAddress: formatAddress(o.deliveryAddress),
      raw: o,
    };
    applyCoordinates(row, (o.restaurantId as { location?: unknown })?.location, o.deliveryAddress);
    all.push(row);
  });

  (groceryOrders || []).forEach((o) => {
    const cust = userLabel(o.customerId);
    const partner = userLabel(o.deliveryPartnerId);
    const row: DispatchRow = {
      _id: o._id as string,
      service: 'grocery',
      ref: String(o.orderNumber),
      customer: cust.name,
      customerPhone: cust.phone,
      partner: partner.name !== '-' ? partner.name : ((o.storeId as { name?: string })?.name || 'Unassigned'),
      status: String(o.status),
      total: Number(o.total || 0),
      createdAt: String(o.createdAt),
      dropoffAddress: formatAddress(o.deliveryAddress),
      raw: o,
    };
    applyCoordinates(row, (o.storeId as { location?: unknown })?.location, o.deliveryAddress);
    all.push(row);
  });

  (porterOrders || []).forEach((o) => {
    const cust = userLabel(o.customerId);
    const partner = userLabel(o.partnerId);
    const pickup = o.pickup as { address?: string; coordinates?: number[] } | undefined;
    const dropoff = o.dropoff as { address?: string; coordinates?: number[] } | undefined;
    const row: DispatchRow = {
      _id: o._id as string,
      service: 'porter',
      ref: String(o.orderNumber || o.bookingNumber),
      customer: cust.name,
      customerPhone: cust.phone,
      partner: partner.name === '-' ? 'Unassigned' : partner.name,
      status: String(o.status),
      total: Number((o.fare as { total?: number })?.total || o.total || 0),
      createdAt: String(o.createdAt),
      pickupAddress: pickup?.address,
      dropoffAddress: dropoff?.address,
      raw: o,
    };
    applyCoordinates(row, pickup, dropoff);
    all.push(row);
  });

  (onDemandBookings || []).forEach((b) => {
    const cust = userLabel(b.customerId);
    const provider = userLabel(b.providerId);
    const row: DispatchRow = {
      _id: b._id as string,
      service: 'on_demand',
      ref: String(b.bookingNumber),
      customer: cust.name,
      customerPhone: cust.phone,
      partner: provider.name === '-' ? 'Unassigned' : provider.name,
      status: String(b.status),
      total: Number(b.total || 0),
      createdAt: String(b.createdAt),
      dropoffAddress: formatAddress(b.address),
      raw: b,
    };
    applyCoordinates(row, undefined, b.address);
    all.push(row);
  });

  return all.map(enrichRow).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}
